import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./HATVCustomViewModal.module.scss";
import Checkbox from "../CustomCheckbox/CustomCheckbox";
import Button from "../Button/Button";
import SavedViewCard from "../SavedViewCard/SavedViewCard";
import { useDispatch } from "react-redux";
import { awardGroupCodeMappingHATV } from "../../../constants/dataConstants";
import { updateViewHatvId } from "../../../redux/slices/HatvViewSlice";
import { useAppSelector } from "../../../redux/hooks";
import { withUtag } from "../../../utils/analytics";
import { RootState } from "../../../redux/store";
import {
  useCreateHatvViewsMutation,
  useUpdateHatvViewsMutation,
  useDeleteHatvViewMutation,
  useLazyGetHatvViewsListQuery,
} from "../../../redux/services/customizeViewHatvAPI";
import { useLazyGetViewDetailsQuery } from "../../../redux/services/customizeViewAPI";

interface QTableCustomViewModalProps {
  onClose: () => void;
  savedViews: ViewListDataType[];
  setViewDetails: any;
  setIsDeletingView: (isLoading: boolean) => void;
  setIsCreatingView: (value: boolean) => void;
}

const QTableCustomViewModal: React.FC<QTableCustomViewModalProps> = ({
  onClose,
  savedViews,
  setViewDetails,
  setIsDeletingView,
  setIsCreatingView,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector(
    (state: RootState) => state.theme.currentTheme
  );
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const DEFAULT_ACCOUNTS = [
    "my_account",
    "red_flag",
    "yellow_flag",
    "not_verified",
  ];
  const DEFAULT_COLUMNS = [
    "group_size",
    "contributing_leg",
    "groupLeader_ppv",
    "volume_flunctuation",
    "eom_order",
    "tenure",
    "bcr_case",
  ];
  const aboColumnObjectMapping: {
    [key: string]: { code: number; value: string };
  } = {
    group_size: {
      code: 1,
      value: t("kpi.groupSize"),
    },
    contributing_leg: {
      code: 2,
      value: t("contributing_leg"),
    },
    groupLeader_ppv: {
      code: 3,
      value: t("kpi.groupLeaderPPV"),
    },
    volume_flunctuation: {
      code: 4,
      value: t("kpi.volumeFlunctuation"),
    },
    eom_order: {
      code: 5,
      value: t("eom_order"),
    },
    tenure: {
      code: 6,
      value: t("kpi.tenure"),
    },
    bcr_case: {
      code: 7,
      value: t("bcr_case"),
    },
  };
  const [selectedAccounts, setSelectedAccounts] =
    useState<string[]>(DEFAULT_ACCOUNTS);

  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(DEFAULT_COLUMNS);
  const [apiColumsList, setAPIColumnList] = useState<number[]>([
    1, 2, 3, 4, 5, 6, 7,
  ]);
  const [selectedColumnList, setSelectedColumnList] = useState(
    DEFAULT_COLUMNS.map((key) => aboColumnObjectMapping[key])
  );
  const [setDefaultView, setSetDefaultView] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [editViewId, setEditViewId] = useState(0);
  const [value, setValue] = useState("");
  const [recentDefaultView, setRecentDefaultView] =
    useState<ViewListDataType>();
  const dispatch = useDispatch();
  const viewId = useAppSelector((state: any) => state.viewIdDetailsHatv.viewId);
  const sectionCount = 3;

  const [createView, { isLoading: isLoadingCreateView }] =
    useCreateHatvViewsMutation();

  const [updateView, { isLoading: isLoadingUpdateView }] =
    useUpdateHatvViewsMutation();

  const [deleteView, { isLoading: isLoadingDeleteView }] =
    useDeleteHatvViewMutation();

  const [viewDetailsAPI] = useLazyGetViewDetailsQuery();

  //   const customViewConfigKPIList = useAppSelector((state: any) => state?.customViewConfigKPIList?.customViewConfigKPIList);
  //   useEffect(() => {
  //     const kpiCodes = customViewConfigKPIList?.filter((item: any) => item?.type === "my-abo")?.map((kpiData: any) => kpiData?.code);

  //     // TODO: dynamically create the object similar to aboColumnObjectMapping at below code and set in local state varible for more dynamic col'm list
  //     // const kpiCodeAndValueObject = customViewConfigKPIList.reduce((acc:any, item:any) => {
  //     //   acc[item.code] = { code: item.code, value: item.value.toString() };
  //     //   return acc;
  //     // }, {});

  //     setAPIColumnList(kpiCodes);
  //   }, [customViewConfigKPIList]);

  const getGroupCodesByAccounts = (
    accountKeys: string[],
    fallbackKey: string
  ): number[] => {
    const filtered = selectedAccounts
      .filter((acc) => accountKeys.includes(acc))
      .map((acc) => awardGroupCodeMappingHATV[acc]);

    return filtered.length > 0
      ? filtered
      : [awardGroupCodeMappingHATV[fallbackKey]];
  };

  const getAccountsByGroupcodes = (
    targetCode: number[],
    accountKeys: string[]
  ): string[] => {
    return accountKeys.filter((key) =>
      targetCode.includes(awardGroupCodeMappingHATV[key])
    );
  };

  const qMonthApprovalAccountGroupCodes = getGroupCodesByAccounts(
    ["my_account", "all_case_evaluator"],
    "my_account"
  );
  const qMonthApprovalViolationGroupCodes = getGroupCodesByAccounts(
    ["red_flag", "yellow_flag"],
    "red_flag"
  );
  const qMonthApprovalStatusGroupCodes = getGroupCodesByAccounts(
    ["verified", "not_verified"],
    "not_verified"
  );

  const qMonthApprovalViolationTypeCodes: number[] = selectedColumns.map(
    (key) => aboColumnObjectMapping[key].code
  );

  useEffect(() => {
    if (savedViews && savedViews?.length >= 1) {
      const views = savedViews?.filter((view) => view._default);
      const defaultViews = views.sort(
        (a, b) =>
          new Date(b.audit.updatedDate).getTime() -
          new Date(a.audit.updatedDate).getTime()
      );
      // Get the most recent default view

      setRecentDefaultView(defaultViews[0]);
    }
  }, [savedViews]);

  const [editIndex, setEditIndex] = useState<number | null>(null);

  const accounts = [
    "myaccounts",
    "all_case_evaluator",
    "red_flag",
    "yellow_flag",
    "verified",
    "not_verified",
  ];

  const columns = [
    "group_size",
    "contributing_leg",
    "groupLeader_ppv",
    "volume_flunctuation",
    "eom_order",
    "tenure",
    "bcr_case",
  ];

  function validateViewName(viewName: any) {
    const trimmedInput = viewName.trim();
    const regex = /^[a-zA-Z0-9 ]+$/;
    return regex.test(trimmedInput);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || validateViewName(value)) {
      setValue(value);
    } else {
    }
  };

  const isEachSectionSelected = () => {
    for (let i = 0; i < sectionCount; i++) {
      const startIndex = i * 2;
      const sectionAccounts = accounts.slice(startIndex, startIndex + 2);
      const hasSelectedAccount = sectionAccounts.some((acc) =>
        selectedAccounts.includes(acc)
      );
      if (!hasSelectedAccount) return false;
    }
    return true;
  };

  const handleAccountSelection = (account: string) => {
    setSelectedAccounts((prev) => {
      const isSelected = prev.includes(account);
      const updatedAccounts = isSelected
        ? prev.filter((item) => item !== account)
        : [...prev, account];

      return updatedAccounts;
    });
  };

  const handleColumnSelection = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((item) => item !== column)
        : [...prev, column]
    );

    setSelectedColumnList((prev) => {
      const columnObject = aboColumnObjectMapping[column]?.value;
      return prev.some((item) => item?.value === columnObject)
        ? prev.filter((item) => item?.value !== columnObject)
        : [...prev, aboColumnObjectMapping[column]];
    });
  };

  const codes: number[] = selectedColumnList?.map((item) => item.code);
  const selectedColumnCodes = `(${codes?.map((item) => `"${item}"`).join(", ")})`;
  const selectedAccountData = `(${selectedAccounts?.map((item) => `"${item}"`).join(", ")})`;

  const handleSetDefaultView = (checked: boolean) => {
    setSetDefaultView(checked);
  };

  const canProceed = () => {
    if (activeStep === 0) {
      return selectedAccounts?.length > 0;
    } else if (activeStep === 1) {
      return selectedColumnList?.length > 0;
    } else if (activeStep === 2) {
      return value?.length > 0;
    }
    return false;
  };

  useEffect(() => {
    setIsChecked(canProceed());
  }, [selectedAccounts, selectedColumnList, activeStep, value]);

  const handleContinue = () => {
    if (canProceed()) {
      if (activeStep === 2) {
        handleSaveView(isUpdate ? updateView : createView);
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };
  const handleApplyColumn = () => {
    onClose();

    setViewDetails({
      qMonthApprovalAccountGroupCodes,
      qMonthApprovalViolationGroupCodes,
      qMonthApprovalStatusGroupCodes,
      qMonthApprovalViolationTypeCodes,
    });

    console.log("Trigger APPLY but not save");
    dispatch(
      updateViewHatvId({
        viewIdDetailsHatv: {
          defaultViewId: null,
          selectedViewId: null,
        },
        applyWithoutSave: true,
      })
    );
  };

  const handleDelete = async (viewId: number) => {
    onClose();
    setIsDeletingView(true);
    const response: any = await deleteView({
      viewId: viewId,
    });

    if (response?.data?.statusCode !== 200) {
      console.error(`Deletion failed for the view : ${viewId}`);
    }
    if (response?.data?.statusCode === 200) {
      dispatch(
        updateViewHatvId({
          viewIdDetailsHatv: {
            defaultViewId: null,
            selectedViewId: null,
          },
        })
      );
    }

    dispatch(
      updateViewHatvId({
        viewIdDetailsHatv: {
          defaultViewId: null,
          selectedViewId: null,
        },
      })
    );

    setIsDeletingView(false);
    //   const response: any = await deleteView({
    //     viewId: viewId,
    //   });

    //   if (response?.data?.statusCode !== 200) {
    //     console.error(`Deletion failed for the view : ${viewId}`);
    //   }
    //   if (response?.data?.statusCode === 200) {
    //     dispatch(
    //       updateViewHatvId({
    //         viewIdDetailsHatv: {
    //           defaultViewId: null,
    //           selectedViewId: 0,
    //         },
    //       })
    //     );
    //   }
  };

  const getViewDetails = async (viewId: number) => {
    const response = await viewDetailsAPI({ viewId });
    if (response?.data?.statusCode === 200) {
      return response?.data?.body?.viewSetting;
    }
  };

  const handleEdit = async (view: any) => {
    setIsUpdate(true);
    setEditViewId(view?.viewId);
    const viewSettings = await getViewDetails(view?.viewId);

    const accountKeys = getAccountsByGroupcodes(
      viewSettings.qMonthApprovalAccountGroupCodes || [],
      ["my_account", "all_case_evaluator"]
    );
    const violationKey = getAccountsByGroupcodes(
      viewSettings.qMonthApprovalViolationGroupCodes || [],
      ["red_flag", "yellow_flag"]
    );
    const statusKey = getAccountsByGroupcodes(
      viewSettings.qMonthApprovalStatusGroupCodes || [],
      ["verified", "not_verified"]
    );

    setValue(view?.name);
    setActiveStep(0);
    setActiveTab(0);
    setSelectedAccounts([...accountKeys, ...violationKey, ...statusKey]);
    const codes = viewSettings?.qMonthApprovalViolationTypeCodes || [];
    const selectedColumnKeys = codes
      .map(
        (code: any) =>
          Object.entries(aboColumnObjectMapping).find(
            ([_, obj]) => obj.code === code
          )?.[0]
      )
      .filter(Boolean);

    setSelectedColumns(selectedColumnKeys);
    const selectedColumnObjects = selectedColumnKeys.map(
      (key: any) => aboColumnObjectMapping[key]
    );
    setSelectedColumnList(selectedColumnObjects);

    setSetDefaultView(view?._default);
  };

  const handleSaveView = async (saveViewAPI: any) => {
    setIsCreatingView(true);
    onClose();
    const createViewPayload = {
      type: "my-q-month-approval",
      name: value,
      _default: setDefaultView,
      viewSetting: {
        qMonthApprovalAccountGroupCodes,
        qMonthApprovalViolationGroupCodes,
        qMonthApprovalStatusGroupCodes,
        qMonthApprovalViolationTypeCodes,
      },
      description: "",
    };

    const updateViewPayload = {
      ...createViewPayload,
      viewId: editViewId,
    };

    const response: any = await saveViewAPI({
      payload: isUpdate ? updateViewPayload : createViewPayload,
    });

    if (response?.data?.statusCode === 200) {
      dispatch(
        updateViewHatvId({
          ...viewId,
          viewIdDetailsHatv: {
            defaultViewId: null,
            selectedViewId: response?.data?.body?.viewId,
          },
        })
      );
      if (isUpdate) {
        setViewDetails({
          qMonthApprovalAccountGroupCodes,
          qMonthApprovalViolationGroupCodes,
          qMonthApprovalStatusGroupCodes,
          qMonthApprovalViolationTypeCodes,
        });
      }
    } else if (response?.data?.statusCode === 400) {
      console.error("Not allow to have view with duplicate name");
    }
    setIsCreatingView(false);
  };

  const handleClickBackOrCancle = () => {
    if (activeStep === 0) {
      onClose();
    } else {
      setActiveStep((prev) => Math.max(prev - 1, 0));
    }
  };

  return (
    <div
      className={`HATV_customize_view_modal ${styles.HATV_customize_view_modal}`}
    >
      <div className={styles.modal_title}>{t("customize_view")}</div>

      <div className={`tabs_wrapper ${styles.tabs_wrapper}`}>
        <div
          className={`tablink ${styles.tablink} ${activeTab === 0 ? `active_tab ${styles.active_tab}` : ""}`}
          onClick={() => {
            setActiveTab(0);
            setActiveStep(activeStep);
          }}
        >
          {t("create_new_view")}
        </div>

        <div
          className={`tablink ${styles.tablink} ${activeTab === 1 ? `active_tab ${styles.active_tab}` : ""}`}
          onClick={() => setActiveTab(1)}
        >
          {t("saved_views")}
        </div>
      </div>

      {activeTab === 0 ? (
        <div className={styles.steps_wrapper}>
          {/* Step 1 */}
          {activeStep === 0 && (
            <div className={styles.steps_inner_wrapper}>
              <div className={`step_title ${styles.step_title}`}>
                <span>{t("step1")}</span>
                <span className={styles.title_subheading}>
                  {t("step1_desc")}
                </span>
              </div>
              {[...Array(3)].map((_, sectionIndex) => {
                const startIndex = sectionIndex * 2;
                const sectionAccounts = accounts?.slice(
                  startIndex,
                  startIndex + 2
                );
                return (
                  <div
                    className={`section ${styles.section}`}
                    key={`section-${sectionIndex}`}
                  >
                    {sectionAccounts?.map((account) => (
                      <div className={styles.checkbox_wrapper} key={account}>
                        <Checkbox
                          checked={selectedAccounts.includes(account)}
                          label={t(account)}
                          checkboxHandler={() =>
                            handleAccountSelection(account)
                          }
                          isDisabled={false}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2 */}
          {activeStep === 1 && (
            <div className={styles.steps_inner_wrapper}>
              <div className={`step_title ${styles.step_title}`}>
                {t("hatv_step2")}
              </div>
              {columns?.map((column) =>
                apiColumsList.includes(aboColumnObjectMapping[column].code) ? (
                  <div className={styles.checkbox_wrapper} key={column}>
                    <Checkbox
                      checked={selectedColumnList.some(
                        (item) =>
                          item.code === aboColumnObjectMapping[column].code
                      )}
                      label={t(aboColumnObjectMapping[column].value)}
                      checkboxHandler={() => handleColumnSelection(column)}
                      isDisabled={false}
                    />
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Step 3 */}
          {activeStep === 2 && (
            <div className={`${styles.steps_wrapper} ${styles.third_step}`}>
              <div className={`step_title ${styles.step_title}`}>
                {t("step3")}
              </div>

              <div
                className={`${styles.input_wrapper} ${
                  isFocused || value ? styles.focused : ""
                }`}
              >
                <input
                  type="text"
                  className={`input_field ${styles.input_field} ${
                    value ? `input_with_text ${styles.input_with_text}` : ""
                  }`}
                  value={value}
                  onChange={handleChange}
                  placeholder={t("name_your_saved_view")}
                  maxLength={50}
                />
              </div>

              <div className={styles.checkbox_wrapper}>
                <Checkbox
                  checked={setDefaultView}
                  label={t("set_as_default_hatv_view")}
                  checkboxHandler={handleSetDefaultView}
                  isDisabled={false}
                />
              </div>
            </div>
          )}

          {/* Step Button Group */}
          <div
            className={`bottom_button_wrapper ${styles.bottom_button_wrapper}`}
          >
            <div className={styles.bottom_button}>
              <Button
                id="backButton"
                onClick={handleClickBackOrCancle}
                label={activeStep === 0 ? t("btnCancel") : t("back")}
                style="unfilled"
              />
              <Button
                id="saveButton"
                onClick={handleContinue}
                label={
                  activeStep === 0 || activeStep === 1
                    ? t("continue")
                    : t("save_and_apply")
                }
                style="filled"
                isDisabled={
                  (activeStep === 0 && !isEachSectionSelected()) ||
                  !canProceed()
                }
              />
            </div>

            {activeStep === 2 && (
              <div className={styles.link_wrapper} onClick={handleApplyColumn}>
                <span className={styles.apply_without_save}>
                  {t("apply_but_dont_save")}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Saved Views Tab
        <div className={styles.saved_views_content}>
          <div className={`step_title ${styles.step_title}`}>
            {t("saved_views_content")}
          </div>
          <div className={styles.saved_view_card_wrapper}>
            {savedViews?.length >= 1 &&
              savedViews.map((view, index) => (
                <SavedViewCard
                  key={index}
                  title={view?.name}
                  isDefault={view?.viewId === recentDefaultView?.viewId}
                  onEdit={() => handleEdit(view)}
                  onDelete={() => handleDelete(view.viewId)}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QTableCustomViewModal;
